/**
 * PARSING A HUMAN 'WHEN' INTO A DAY — for the quick-add `^` token (Track B).
 *
 * There is no date library in the app and this wants none. It understands a
 * small, EXPLICIT vocabulary and returns a plain `YYYY-MM-DD` day, or `null`
 * when it does not recognise the input — so the caller records a MISS and never
 * guesses, the same contract every other quick-add token keeps. A date the app
 * cannot be sure of is worse than no date.
 *
 *   2026-09-18        an ISO day (returned as-is when it is a real calendar day)
 *   today, tod        today
 *   tomorrow, tom     tomorrow
 *   mon … sun         the coming weekday (today, if today is that day)
 *   +3d   +2w         three days / two weeks from today
 *   eom               the last day of this month
 *
 * DAYS ARE LOCAL. A due date is a day on the person's own calendar, so every
 * answer is computed from LOCAL midnight and never touches UTC — which is what
 * would slide "tomorrow" onto the wrong date for anyone typing near midnight.
 *
 * `todayMs` is a parameter, defaulting to now, so the whole thing is pure and a
 * test can pin "today" to a fixed day.
 */

const WEEKDAYS: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

// ONE WORDED MONTH, EVERYWHERE. September is "Sept" (four letters) to match
// `utils.asDate`, which formats via en-GB `month:'short'` and emits "Sept" — so a
// date field, a card and a status banner all read "12 Sept 2026", never "Sep" on
// one surface and "Sept" on another (design rule R16). Every other month is three
// letters either way. The parser keeps "sep" as an alias (see MONTH_WORDS) so a
// person can still type it. Exported so no surface mints its own and drifts back
// to "Sep"; everything that writes a worded month imports this.
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

/** A Date, formatted as `YYYY-MM-DD` from its LOCAL parts. */
function toDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Local midnight of an instant, as a Date days can be added to. */
function midnight(ms: number): Date {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** True only for a real calendar day (rejects 2026-13-40 and 2026-02-30). */
function isRealIsoDay(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/** Parse a human 'when' into a `YYYY-MM-DD` day, or null if not understood. */
export function parseWhen(input: string, todayMs: number = Date.now()): string | null {
  const s = input.trim().toLowerCase();
  if (!s) return null;

  if (isRealIsoDay(s)) return s;

  if (s === 'today' || s === 'tod') return toDay(midnight(todayMs));

  if (s === 'tomorrow' || s === 'tom' || s === 'tmr') {
    const d = midnight(todayMs);
    d.setDate(d.getDate() + 1);
    return toDay(d);
  }

  if (s === 'eom') {
    const d = midnight(todayMs);
    return toDay(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  }

  const rel = s.match(/^\+(\d{1,3})(d|w)$/);
  if (rel) {
    const n = Number(rel[1]) * (rel[2] === 'w' ? 7 : 1);
    const d = midnight(todayMs);
    d.setDate(d.getDate() + n);
    return toDay(d);
  }

  if (s in WEEKDAYS) {
    const d = midnight(todayMs);
    const delta = (WEEKDAYS[s] - d.getDay() + 7) % 7; // 0..6, today if today is that weekday
    d.setDate(d.getDate() + delta);
    return toDay(d);
  }

  return null;
}

/** A short, locale-free label for a `YYYY-MM-DD` day — "15 Sep". */
export function shortDay(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}

/** The same day with its year — "15 Sep 2026". What a date FIELD shows, where
 *  the year matters because the value is being edited, not merely glanced at. */
export function humanDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

const MONTH_WORDS: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  const full = ['january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'];
  MONTHS.forEach((m, i) => { map[m.toLowerCase()] = i + 1; });   // jan..aug, sept, oct..dec
  full.forEach((m, i) => { map[m] = i + 1; });                    // january..december
  map['sep'] = 9;  // "Sept" is the display form, but a person may still type "sep"
  return map;
})();

/** Build `YYYY-MM-DD` from parts, or null when it is not a real calendar day. */
function buildIso(y: number, m: number, d: number): string | null {
  const iso = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return isRealIsoDay(iso) ? iso : null;
}

/**
 * PARSING A DATE A PERSON TYPED INTO A FIELD — the forgiving companion to
 * `parseWhen`, for the issue card's Start/Target boxes (Track B).
 *
 * Why this exists: the native `<input type="date">` hands back an EMPTY string
 * for anything that is not a fully-formed date, so a half-typed or mis-keyed day
 * saved as nothing at all and the card silently lost the date. This reads what a
 * person actually types and returns a real day or null — and the field shows the
 * day it resolved to, so a null is visible, never a silent guess.
 *
 * It understands everything `parseWhen` does (ISO, today/tomorrow, weekdays,
 * `+3d`/`+2w`, `eom`) and, on top of that, the way people write a plain date:
 *
 *   15 Sep · 15 Sep 2026 · 15 September      day then month
 *   Sep 15 · September 15, 2026              month then day
 *   15/09/2026 · 15.9.2026 · 9-15-2026       numeric, any of / . -
 *   2026/09/15                               a slashed ISO day
 *
 * A YEAR left off is taken as the current one — and shown back, so if that is not
 * the year meant, the person sees it and types the year. A numeric date is read
 * day-first, EXCEPT where a part over twelve can only be the day, which pins the
 * order without guessing.
 */
export function parseDateInput(input: string, todayMs: number = Date.now()): string | null {
  const known = parseWhen(input, todayMs);           // ISO, words, offsets, weekdays
  if (known) return known;

  const s = input.trim().toLowerCase().replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  const thisYear = new Date(todayMs).getFullYear();

  // day then month name, or month name then day — the year optional.
  let m = s.match(/^(\d{1,2}) ([a-z]{3,9})(?: (\d{4}))?$/);
  if (m && m[2] in MONTH_WORDS) return buildIso(m[3] ? Number(m[3]) : thisYear, MONTH_WORDS[m[2]], Number(m[1]));
  m = s.match(/^([a-z]{3,9}) (\d{1,2})(?: (\d{4}))?$/);
  if (m && m[1] in MONTH_WORDS) return buildIso(m[3] ? Number(m[3]) : thisYear, MONTH_WORDS[m[1]], Number(m[2]));

  // numeric, separated by / . or - . A four-digit part is unambiguously the year.
  const n = s.split(/[/.\-]/).map((p) => p.trim());
  if (n.length === 3 && n.every((p) => /^\d{1,4}$/.test(p))) {
    const [a, b, c] = n.map(Number);
    if (n[0].length === 4) return buildIso(a, b, c);            // 2026/09/15 — year first
    const year = n[2].length === 4 ? c : (c < 100 ? 2000 + c : c);
    // day-first, unless a part over twelve can only be the day.
    if (a > 12 || b <= 12) return buildIso(year, b, a);         // a = day, b = month
    return buildIso(year, a, b);                                // a = month, b = day
  }
  if (n.length === 2 && n.every((p) => /^\d{1,2}$/.test(p))) {
    const [a, b] = n.map(Number);
    if (a > 12 || b <= 12) return buildIso(thisYear, b, a);     // day/month
    return buildIso(thisYear, a, b);                            // month/day
  }

  return null;
}

/** The three orders a numeric date can be written in — day-, month- or year-first. */
export type DateOrder = 'dmy' | 'mdy' | 'ymd';

/**
 * ONE IMPORTED DATE CELL → ISO, READ IN THE ORDER CHOSEN FOR THE FILE.
 *
 * The fiddly half of bringing a schedule in is that 03/04 is the third of April
 * to half the world and the fourth of March to the other half. A spreadsheet
 * writes its dates one way throughout, so the file is read in a SINGLE chosen
 * order rather than guessed cell by cell — which is how a whole import lands
 * with every day and month transposed. A date written in words ("15 Sep 2026")
 * or year-first ("2026-09-15") is not ambiguous and is read as written whatever
 * the order says; only a bare numeric date honours it. Anything unreadable
 * returns null, so the row simply arrives without that date, never with a wrong
 * one.
 */
export function parseImportDate(
  input: string,
  order: DateOrder,
  todayMs: number = Date.now()
): string | null {
  const s = (input ?? '').trim();
  if (!s) return null;
  const p = s.split(/[/.\-]/).map((x) => x.trim());
  const numeric = p.length === 3 && p.every((x) => /^\d{1,4}$/.test(x));
  // Words, offsets, ISO and "15 Sep" are unambiguous — parseDateInput has them.
  if (!numeric) return parseDateInput(s, todayMs);
  const [a, b, c] = p.map(Number);
  // A four-digit part is the year wherever it sits, and settles the order itself.
  if (p[0].length === 4) return buildIso(a, b, c);
  const year = p[2].length === 4 ? c : c < 100 ? 2000 + c : c;
  // A part over twelve can only be the day — a hard fact that overrides the
  // chosen order, so an unambiguous value is never dropped for disagreeing with
  // it. The order decides only where the date genuinely could be read either way.
  if (a > 12 && b <= 12) return buildIso(year, b, a);          // day, month
  if (b > 12 && a <= 12) return buildIso(year, a, b);          // month, day
  if (order === 'ymd') return buildIso(a < 100 ? 2000 + a : a, b, c);
  if (order === 'mdy') return buildIso(year, a, b);            // month, day
  return buildIso(year, b, a);                                 // day, month
}

/**
 * WHICH ORDER A COLUMN OF DATES IS WRITTEN IN, AND WHETHER THE FILE SETTLES IT.
 *
 * Day-first is what this product reads in, so it is the default. A value with a
 * part over twelve (13/09) can only be read one way and proves the order; a
 * value written year-first (2026-09-15) proves ymd. If every numeric value could
 * be read either way, the column is genuinely ambiguous and the person is asked
 * — `sure` is false. A column with no ambiguous numeric dates (all words, all
 * ISO, or empty) needs no question, so `sure` is true.
 */
export function guessDateOrder(values: readonly string[]): { order: DateOrder; sure: boolean } {
  let ambiguous = false;
  let forcedDmy = false;
  let forcedMdy = false;
  let sawYmd = false;
  for (const raw of values) {
    const s = (raw ?? '').trim();
    if (!s) continue;
    const p = s.split(/[/.\-]/).map((x) => x.trim());
    if (p.length !== 3 || !p.every((x) => /^\d{1,4}$/.test(x))) continue;
    if (p[0].length === 4) { sawYmd = true; continue; }
    const a = Number(p[0]);
    const b = Number(p[1]);
    if (a > 12 && b <= 12) forcedDmy = true;
    else if (b > 12 && a <= 12) forcedMdy = true;
    else ambiguous = true;
  }
  if (forcedDmy && !forcedMdy) return { order: 'dmy', sure: true };
  if (forcedMdy && !forcedDmy) return { order: 'mdy', sure: true };
  if (sawYmd && !forcedDmy && !forcedMdy && !ambiguous) return { order: 'ymd', sure: true };
  return { order: 'dmy', sure: !ambiguous };
}

export interface DueSuggestion {
  /** What to put after `^` when chosen — resolves back through parseWhen. */
  token: string;
  /** What the person reads — "Today", "This Friday". */
  label: string;
  /** The day it resolves to, short — "15 Sep". */
  sub: string;
}

/** The handful of dates the `^` menu offers, each a token parseWhen understands. */
export function dueSuggestions(todayMs: number = Date.now()): DueSuggestion[] {
  const items: { token: string; label: string }[] = [
    { token: 'today', label: 'Today' },
    { token: 'tomorrow', label: 'Tomorrow' },
    { token: 'fri', label: 'This Friday' },
    { token: '+1w', label: 'Next week' },
    { token: 'eom', label: 'End of month' },
  ];
  return items.map((i) => ({ ...i, sub: shortDay(parseWhen(i.token, todayMs)!) }));
}
